import { Button } from '@hologram-dimension/button';
import { Icon } from '@hologram-dimension/icon';
import { Panel } from '@hologram-dimension/panel';
import {
  useOpenSessions,
  useSimsCount,
  useSimStatuses,
} from '@hologram-hyper-dashboard/components';
import clsx from 'clsx/lite';
import { PAGE_VIEW } from 'common-js/analytics/actionTypes';
import { sendAnalyticsEvent } from 'common-js/analytics/analytics';
import { getUserContextDataMemoized } from 'common-js/api/util';
import { FullHeightCalculatorHOC, HoloHelmet } from 'common-js/components';
import * as DeviceStates from 'common-js/constants/deviceStates';
import {
  SIM_INVENTORY_ACTIVE,
  USAGE_BY_DAY,
  USAGE_IN_TESTING,
  WELCOME,
  withContext,
} from 'common-js/constants/paths';
import useAppSelector from 'common-js/hooks/useAppSelector';
import { selectFirstName, selectHasSeenFleetHome } from 'common-js/reducers/account/selectors';
import { getUserPermissions } from 'common-js/reducers/organization/selectors';
import {
  selectHasFleetHistoric,
  selectHasFleetStatus,
  selectHasInflight,
  selectHasPredeploymentTesting,
} from 'common-js/reducers/releaseFlag/selectors';
import { BILLING_VISIBLE } from 'common-js/utils/allPermissions';
import { getDeviceStatusText } from 'common-js/utils/deviceStatus';
import { Loader } from 'holokit';
import { FleetHomeEyebrow, MetricPanel, MetricPanelFullWidth } from 'home/components';
import { useEffect, useMemo, type FC } from 'react';
import { browserHistory } from 'react-router';

const EyebrowAndHero: FC = () => {
  const firstName = useAppSelector(selectFirstName);
  const hasSeenFleetHome = useAppSelector(selectHasSeenFleetHome);
  const hasInflight = useAppSelector(selectHasInflight);

  return (
    <FleetHomeEyebrow
      firstName={firstName}
      hasSeenFleetHome={hasSeenFleetHome}
      showInflight={hasInflight}
    />
  );
};

const FleetHome: FC = () => {
  // Redux state
  const hasFleetStatus = useAppSelector(selectHasFleetStatus);
  const userPermissions = useAppSelector(getUserPermissions);
  const hasBillingVisible = userPermissions.includes(BILLING_VISIBLE);
  const hasPredeploymentTesting = useAppSelector(selectHasPredeploymentTesting);
  const historicFlag = useAppSelector(selectHasFleetHistoric);
  const userContext = useAppSelector(getUserContextDataMemoized);
  const orgId = userContext.isInOrgContext ? userContext.orgId : userContext.userOrgId;

  useEffect(() => {
    sendAnalyticsEvent({ type: PAGE_VIEW, data: { page: 'Fleet Home' } });
  }, []);

  const {
    networkStatusGroups,
    isLoading: simStatusesLoading,
    error: simStatusesError,
  } = useSimStatuses();

  const canLoadSimsCount = hasFleetStatus && !simStatusesError && !simStatusesLoading;

  const { count: activeSimsCount, isLoading: activeSimsCountLoading } = useSimsCount(
    { organizationId: orgId, networkStatus: networkStatusGroups?.active },
    canLoadSimsCount
  );

  const { count: pausedUserSimsCount, isLoading: pausedUserSimsCountLoading } = useSimsCount(
    { organizationId: orgId, networkStatus: [DeviceStates.PAUSED_USER] },
    canLoadSimsCount
  );

  const { count: pausedSysSimsCount, isLoading: pausedSysSimsCountLoading } = useSimsCount(
    { organizationId: orgId, networkStatus: [DeviceStates.PAUSED_SYS] },
    canLoadSimsCount
  );
  const { count: predeploymentSimsCount, isLoading: predeploymentSimsCountLoading } = useSimsCount(
    {
      organizationId: orgId,
      networkStatus: [
        DeviceStates.TEST_ACTIVATE,
        DeviceStates.TEST_AUTOACTIVATE,
        DeviceStates.TEST_HIBERNATE,
      ],
    },
    canLoadSimsCount
  );

  const { count: totalSimsCount, isLoading: totalSimsCountLoading } = useSimsCount(
    { organizationId: orgId },
    canLoadSimsCount
  );

  const { count: liveSimsCount, isLoading: liveSimsCountLoading } = useSimsCount(
    { organizationId: orgId, networkStatus: networkStatusGroups?.active },
    canLoadSimsCount
  );

  const { deviceIdsWithOpenSessions, isLoading: openSessionsLoading } = useOpenSessions(
    orgId,
    canLoadSimsCount
  );

  const { count: inactiveSimsCount, isLoading: inactiveSimsCountLoading } = useSimsCount(
    { organizationId: orgId, networkStatus: [DeviceStates.INACTIVE] },
    canLoadSimsCount
  );

  const { count: hibernatingSimsCount, isLoading: hibernatingSimsCountLoading } = useSimsCount(
    { organizationId: orgId, networkStatus: [DeviceStates.INACTIVE_TESTED] },
    canLoadSimsCount
  );

  const isLoading =
    activeSimsCountLoading ||
    openSessionsLoading ||
    predeploymentSimsCountLoading ||
    pausedSysSimsCountLoading ||
    pausedUserSimsCountLoading ||
    liveSimsCountLoading ||
    totalSimsCountLoading ||
    inactiveSimsCountLoading ||
    hibernatingSimsCountLoading;

  // TODO: This placement of the redirect leads to flickering, need to move it to a wrapper with the loader
  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (activeSimsCount < 1 && predeploymentSimsCount < 1) {
      browserHistory.push(withContext(WELCOME));
    } else if (!hasFleetStatus) {
      browserHistory.push(withContext(SIM_INVENTORY_ACTIVE));
    }
  }, [
    activeSimsCount,
    hasFleetStatus,
    isLoading,
    liveSimsCount,
    pausedSysSimsCount,
    pausedUserSimsCount,
    predeploymentSimsCount,
  ]);

  const totalDeviceSegments = useMemo<Array<any>>(() => {
    const newTotalDeviceSegments: Array<any> = [
      {
        title: 'Paused SIMs',
        toolTipText:
          "Breakdown of your organization's paused SIMs that are not currently passing any data. They have been paused manually by a member of your team, or by the system due to a billing issue or data limit reached.",
        devices: [
          {
            // TODO: Iron out status naming conventions across the dashboard so this name doesn't have to be hard coded
            name: 'Paused by user',
            deviceStateFilters: [DeviceStates.PAUSED_USER],
            number: pausedUserSimsCount,
            color: 'var(--dds-color-palette-pink-70)',
            icon: <Icon name="StatusPaused" size="small" />,
          },
          {
            name: getDeviceStatusText(DeviceStates.PAUSED_SYS),
            deviceStateFilters: [DeviceStates.PAUSED_SYS],
            number: pausedSysSimsCount,
            color: 'var(--dds-color-palette-pink-44)',
            icon: <Icon name="StatusPaused" size="small" fill="DdsColorPaletteWhite" />,
          },
        ],
      },
    ];

    if (hasPredeploymentTesting) {
      let inactiveHibernateMetric: any | null = null;

      const availableCount = inactiveSimsCount ?? 0;
      const hibernatingCount = hibernatingSimsCount ?? 0;

      if (availableCount > 0 || hibernatingCount > 0) {
        inactiveHibernateMetric = {
          name: 'Available',
          deviceStateFilters: [DeviceStates.INACTIVE_TESTED, DeviceStates.INACTIVE],
          number: availableCount + hibernatingCount,
          color: 'var(--dds-color-palette-grey-88)',
          icon: <Icon name="StatusInventory" size="small" />,
        };
      }
      const preflightSegmentDevices: any = [
        {
          name: getDeviceStatusText(DeviceStates.TEST_AUTOACTIVATE),
          deviceStateFilters: [
            'TEST-ACTIVATE-PENDING',
            'TEST-AUTOACTIVATE-PENDING',
            'TEST-HIBERNATE-PENDING',
            'TEST-ACTIVATE',
            'TEST-HIBERNATE',
            'TEST-AUTOACTIVATE',
          ],
          number: predeploymentSimsCount,
          color: 'var(--dds-color-palette-purple-75)',
          icon: <Icon name="StatusTesting" size="small" />,
        },
      ];

      if (inactiveHibernateMetric) {
        preflightSegmentDevices.push(inactiveHibernateMetric);
      }

      if (hasBillingVisible) {
        newTotalDeviceSegments.push({
          title: 'Pre-deployment SIMs',
          color: 'var(--dds-color-palette-grey-80)',
          icon: <Icon name="StatusTesting" size="small" />,
          toolTipText:
            'Your subset of preconfigured SIMs with Test mode enabled that have access to free data and SMS messages prior to full activation.',
          devices: preflightSegmentDevices,
        });
      }
    }

    return newTotalDeviceSegments;
  }, [
    hasBillingVisible,
    hasPredeploymentTesting,
    hibernatingSimsCount,
    inactiveSimsCount,
    pausedSysSimsCount,
    pausedUserSimsCount,
    predeploymentSimsCount,
  ]);

  const oneDeviceSegment = totalDeviceSegments.length === 1;

  return (
    <FullHeightCalculatorHOC accountForDrawer backgroundColor="var(--dds-color-palette-grey-3)">
      <HoloHelmet title="Home" />
      <Loader isLoading={isLoading} />
      <div className="FleetHome">
        <div className="FleetHome__hero">
          <section className="FleetHome__hero__container">
            {totalSimsCount > 0 && <EyebrowAndHero />}
          </section>
        </div>
        <section className="FleetHome__body">
          <div className="FleetHome__body__row">
            <Panel className="FleetHome__hero__metrics MetricPanel__body" header="Active SIMs">
              <MetricPanelFullWidth
                keyMetric={{
                  name: 'Active SIMs',
                  number: activeSimsCount,
                }}
                comparingDataFromPeriod="Last week"
                historicFlag={historicFlag}
                // TODO: revisit whether these states are represented correctly
                deviceSegments={[
                  {
                    name: getDeviceStatusText(DeviceStates.CONNECTED),
                    deviceStateFilters: [DeviceStates.CONNECTED],
                    number: deviceIdsWithOpenSessions?.size ?? 0,
                    icon: <Icon name="StatusConnected" size="small" />,
                    color: 'var(--dds-color-fill-notification-highlight)',
                  },
                  {
                    name: getDeviceStatusText(DeviceStates.LIVE),
                    deviceStateFilters: [DeviceStates.LIVE],
                    number: liveSimsCount - (deviceIdsWithOpenSessions?.size ?? 0),
                    icon: <Icon name="StatusReady" size="small" fill="DdsColorPaletteWhite" />,
                    color: 'var(--dds-color-palette-teal-40)',
                  },
                  {
                    name: 'Paused',
                    deviceStateFilters: [DeviceStates.PAUSED_USER, DeviceStates.PAUSED_SYS],
                    number: (pausedSysSimsCount ?? 0) + (pausedUserSimsCount ?? 0),
                    icon: <Icon name="StatusPaused" size="small" />,
                    color: 'var(--dds-color-palette-pink-70)',
                  },
                ]}
                isLoading={isLoading}
              />
            </Panel>
          </div>
          <div
            className={clsx(
              'FleetHome__body__row',
              'FleetHome__body__row--metrics',
              oneDeviceSegment && 'FleetHome__body__row--metrics--single'
            )}
          >
            {totalDeviceSegments.map((segment, i) => (
              <MetricPanel
                buttonClasses={clsx(
                  'FleetHome__body__panel__buttons__button',
                  oneDeviceSegment && 'FleetHome__body__panel__buttons__button--fullWidth'
                )}
                comparingDataFromPeriod="Last week"
                deviceSegments={segment.devices}
                isLoading={isLoading}
                key={`FleetHome__body__metricPanel--${segment.title ?? i}`}
                metricTitle={segment.title}
                toolTipText={segment.toolTipText}
                historicFlag={historicFlag}
              />
            ))}
          </div>
          <div className="FleetHome__body__row">
            <Panel
              noContentPadding
              headerActions={
                <>
                  <Button
                    variant="secondary"
                    to={withContext(USAGE_BY_DAY)}
                    iconEnd="ArrowEastShort"
                    inverse
                  >
                    Data usage for active SIMs
                  </Button>
                  {hasPredeploymentTesting && (
                    <Button
                      variant="secondary"
                      to={withContext(USAGE_IN_TESTING)}
                      iconEnd="ArrowEastShort"
                      inverse
                    >
                      Data usage for pre-deployment SIMs
                    </Button>
                  )}
                </>
              }
              header={<span className="DataUsedPanel__panelTitle">Data Usage</span>}
              className="DataUsedPanel"
            >
              {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
              <></>
            </Panel>
          </div>
        </section>
      </div>
    </FullHeightCalculatorHOC>
  );
};

export default FleetHome;
